/**
 * TimeSystem — In-game time tracking and day/night cycle management.
 * Time is stored in WorldState.time as { day, hour, minute }.
 * Hours and minutes are always integers; fractional hours are
 * distributed into whole minutes during advanceTime().
 *
 * Day/Night boundaries (from docs/systems/world.md):
 *   Dawn   06:00 – 08:00
 *   Day    08:00 – 18:00
 *   Dusk   18:00 – 20:00
 *   Night  20:00 – 06:00
 *
 * @module TimeSystem
 */

import DebugLog from '../debug/DebugLog.js';
import WorldState from '../world/WorldState.js';

// ─── Terrain movement costs (hours per tile) ──────────────────────────────────
// These match the specification in docs/systems/world.md.

const MOVE_COSTS = {
  road:      0.15,
  spellroad: 0.10,
  plains:    0.25,
  grass:     0.25,
  forest:    0.40,
  hills:     0.50,
  mountains: 0.80,
  mountain:  0.80,
  swamp:     0.60,
  snow:      0.50,
  coast:     0.25,
  sand:      0.30,
  water:     Infinity, // impassable
  default:   0.50,
};

// Night multiplier applied to all movement costs (doc: 1.5×).
const NIGHT_MOVE_MULTIPLIER = 1.5;

// ─── TimeSystem ───────────────────────────────────────────────────────────────

const TimeSystem = {
  /**
   * Get the current in-game time from WorldState.
   * Initializes to day 1, 08:00 if not yet set.
   * @returns {{ day: number, hour: number, minute: number }}
   */
  getTime() {
    if (!WorldState.time) {
      WorldState.time = { day: 1, hour: 8, minute: 0 };
    }
    return WorldState.time;
  },

  /**
   * Advance in-game time by the given number of hours (may be fractional).
   * Updates WorldState.time in place and emits 'time:advanced' on EventBus.
   *
   * @param {number} hours — Hours to advance; may be fractional (e.g., 0.25 = 15 min)
   * @returns {{ dayChanged: boolean, nightFell: boolean, dawnBroke: boolean }}
   *   dayChanged  — true if one or more full days rolled over
   *   nightFell   — true if the hour crossed from < 20 to >= 20
   *   dawnBroke   — true if the hour crossed from >= 20 (or < 6) to >= 6
   */
  advanceTime(hours) {
    const time = this.getTime();

    const wasNight = this.isNight();
    const prevHour = time.hour;

    // Convert hours to total minutes and add to current position
    const totalMinutes = time.minute + Math.round(hours * 60);
    const extraHours   = Math.floor(totalMinutes / 60);
    time.minute        = totalMinutes % 60;

    let dayChanged  = false;
    let nightFell   = false;
    let dawnBroke   = false;

    if (extraHours > 0) {
      const newTotalHours = time.hour + extraHours;
      const newHour       = newTotalHours % 24;
      const daysAdvanced  = Math.floor(newTotalHours / 24);

      if (daysAdvanced > 0) {
        time.day  += daysAdvanced;
        dayChanged  = true;
        DebugLog.info(`[time] Day advanced to ${time.day}`);
      }

      // Detect night → dawn transition: old hour < 6 or >= 20, new hour >= 6 and < 20
      const oldIsNight = prevHour >= 20 || prevHour < 6;
      const newIsDay   = newHour >= 6 && newHour < 20;
      if (oldIsNight && newIsDay) {
        dawnBroke = true;
        DebugLog.info('[time] Dawn broke');
      }

      // Detect day → night transition: old hour < 20, new hour >= 20
      const oldIsDay  = prevHour >= 6 && prevHour < 20;
      const newIsNight = newHour >= 20 || newHour < 6;
      if (oldIsDay && newIsNight) {
        nightFell = true;
        DebugLog.info('[time] Night fell');
      }

      time.hour = newHour;
    }

    DebugLog.info(
      `[time] Time advanced ${hours.toFixed(2)}h → ${this.formatTime()}; ` +
      `dayChanged=${dayChanged} nightFell=${nightFell} dawnBroke=${dawnBroke}`
    );

    // Notify listeners
    if (typeof window !== 'undefined' && window.__eventBus) {
      window.__eventBus.emit?.('time:advanced', { time: { ...time }, dayChanged, nightFell, dawnBroke });
    }

    return { dayChanged, nightFell, dawnBroke };
  },

  /**
   * Test whether it is currently night time (20:00 – 06:00, exclusive).
   * @returns {boolean}
   */
  isNight() {
    const { hour } = this.getTime();
    return hour >= 20 || hour < 6;
  },

  /**
   * Test whether it is currently dawn (06:00 – 08:00).
   * @returns {boolean}
   */
  isDawn() {
    const { hour } = this.getTime();
    return hour >= 6 && hour < 8;
  },

  /**
   * Test whether it is currently daytime (08:00 – 18:00).
   * @returns {boolean}
   */
  isDay() {
    const { hour } = this.getTime();
    return hour >= 8 && hour < 18;
  },

  /**
   * Test whether it is currently dusk (18:00 – 20:00).
   * @returns {boolean}
   */
  isDusk() {
    const { hour } = this.getTime();
    return hour >= 18 && hour < 20;
  },

  /**
   * Return the name of the current time-of-day period.
   * @returns {'night'|'dawn'|'day'|'dusk'}
   */
  getPeriod() {
    if (this.isNight()) return 'night';
    if (this.isDawn())  return 'dawn';
    if (this.isDusk())  return 'dusk';
    return 'day';
  },

  /**
   * Get the movement cost in hours for a given terrain type.
   * Applies the night multiplier automatically when it is night.
   *
   * @param {string} terrain — Terrain type string (e.g. 'forest', 'road')
   * @param {{ applyNightPenalty?: boolean }} [options]
   * @returns {number} Hours per tile
   */
  getMoveCost(terrain, options = {}) {
    const base    = MOVE_COSTS[terrain] ?? MOVE_COSTS.default;
    const applyNP = options.applyNightPenalty !== false; // default true
    const mult    = (applyNP && this.isNight()) ? NIGHT_MOVE_MULTIPLIER : 1.0;
    return base * mult;
  },

  /**
   * Format the current time for HUD display: "Day 3, 14:30"
   * @returns {string}
   */
  formatTime() {
    const { day, hour, minute } = this.getTime();
    const hh = String(hour).padStart(2, '0');
    const mm = String(minute).padStart(2, '0');
    return `Day ${day}, ${hh}:${mm}`;
  },

  /**
   * Set the time directly (for debug / save-load).
   * @param {{ day?: number, hour?: number, minute?: number }} partial
   */
  setTime(partial) {
    const time = this.getTime();
    if (partial.day    !== undefined) time.day    = Math.max(1, Math.floor(partial.day));
    if (partial.hour   !== undefined) time.hour   = Math.max(0, Math.min(23, Math.floor(partial.hour)));
    if (partial.minute !== undefined) time.minute = Math.max(0, Math.min(59, Math.floor(partial.minute)));
    DebugLog.info(`[time] Time set to ${this.formatTime()}`);
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__debug = window.__debug ?? {};

  /**
   * Set in-game hour (0–23).
   * @param {number} hour
   */
  window.__debug.setTime = (hour) => {
    TimeSystem.setTime({ hour });
    console.info(`[debug] setTime → ${TimeSystem.formatTime()}`);
  };

  /**
   * Get current time as a formatted string.
   * @returns {string}
   */
  window.__debug.getTime = () => TimeSystem.formatTime();
}

export default TimeSystem;
